import { useEffect, useCallback, useState } from "react";
import { useLang } from "@/lib/LanguageContext";

/**
 * Lightbox — fullscreen editorial enlargement for portfolio images.
 * Prev/next arrows are floating side controls (centered vertically) to avoid
 * collisions with the Emergent badge or other corner UI.
 * When a project provides `images: []` with more than one entry, a thumbnail
 * strip appears under the main image so visitors can flip between shots.
 */
export default function Lightbox({ projects, index, onClose, onPrev, onNext }) {
    const { t, lang } = useLang();
    const project = index != null ? projects[index] : null;

    // Compose a media strip of images + optional video (video always last).
    const mediaItems = (() => {
        if (!project) return [];
        const imgs =
            project.images && project.images.length
                ? project.images.map((src) => ({ type: "image", src }))
                : project.image
                ? [{ type: "image", src: project.image }]
                : [];
        if (project.video) {
            imgs.push({
                type: "video",
                src: project.video,
                poster: project.image,
            });
        }
        return imgs;
    })();

    const [mediaIdx, setMediaIdx] = useState(0);

    // Reset the gallery cursor whenever the visitor navigates to a different project
    useEffect(() => {
        setMediaIdx(0);
    }, [index]);

    const handleKey = useCallback(
        (e) => {
            if (!project) return;
            if (e.key === "Escape") onClose();
            else if (e.key === "ArrowLeft") onPrev();
            else if (e.key === "ArrowRight") onNext();
        },
        [project, onClose, onPrev, onNext],
    );

    useEffect(() => {
        if (!project) return undefined;
        document.addEventListener("keydown", handleKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", handleKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [project, handleKey]);

    if (!project) return null;

    const hint =
        lang === "tr"
            ? "Esc ile kapat · ← → ile gez"
            : lang === "nl"
            ? "Esc om te sluiten · ← → om te bladeren"
            : "Esc to close · ← → to navigate";

    return (
        <div
            data-testid="portfolio-lightbox"
            role="dialog"
            aria-modal="true"
            className="fixed inset-0 z-[80] bg-navy/95 backdrop-blur-md flex flex-col"
            onClick={onClose}
        >
            {/* Top bar */}
            <div className="flex items-center justify-between px-6 md:px-10 py-5 border-b border-ivory/10">
                <p className="overline text-ivory/55">
                    {String(index + 1).padStart(2, "0")} /{" "}
                    {String(projects.length).padStart(2, "0")} —{" "}
                    {t.portfolio.title}
                </p>
                <button
                    type="button"
                    data-testid="lightbox-close"
                    aria-label="Close"
                    onClick={(e) => {
                        e.stopPropagation();
                        onClose();
                    }}
                    className="overline text-ivory hover:text-terracotta transition-colors"
                >
                    Close ✕
                </button>
            </div>

            {/* Body */}
            <div
                className="flex-1 grid grid-cols-12 gap-6 md:gap-10 px-6 md:px-20 py-8 overflow-auto"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Image / Video */}
                <div className="col-span-12 md:col-span-8 flex flex-col items-center justify-center gap-4">
                    {(() => {
                        const current = mediaItems[mediaIdx];
                        if (!current) return null;
                        if (current.type === "video") {
                            return (
                                <video
                                    key={current.src}
                                    data-testid="lightbox-video"
                                    src={current.src}
                                    poster={current.poster}
                                    controls
                                    autoPlay
                                    playsInline
                                    loop
                                    className="max-h-[76vh] w-auto max-w-full object-contain shadow-2xl bg-black"
                                >
                                    <track kind="captions" />
                                </video>
                            );
                        }
                        return (
                            <img
                                key={current.src}
                                src={current.src}
                                alt={project.title[lang]}
                                data-testid="lightbox-image"
                                className="max-h-[76vh] w-auto max-w-full object-contain shadow-2xl"
                            />
                        );
                    })()}

                    {mediaItems.length > 1 && (
                        <div
                            data-testid="lightbox-thumbs"
                            className="flex items-center gap-3 flex-wrap justify-center"
                        >
                            {mediaItems.map((m, i) => (
                                <button
                                    type="button"
                                    key={m.src}
                                    data-testid={`lightbox-thumb-${i}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setMediaIdx(i);
                                    }}
                                    aria-label={
                                        m.type === "video"
                                            ? "Play video"
                                            : `View image ${i + 1}`
                                    }
                                    className={`relative w-16 h-16 md:w-20 md:h-20 overflow-hidden border transition-all ${
                                        i === mediaIdx
                                            ? "border-terracotta opacity-100"
                                            : "border-ivory/25 opacity-60 hover:opacity-100 hover:border-ivory/50"
                                    }`}
                                >
                                    <img
                                        src={m.type === "video" ? m.poster : m.src}
                                        alt=""
                                        className="w-full h-full object-cover"
                                    />
                                    {m.type === "video" && (
                                        <span
                                            aria-hidden="true"
                                            className="absolute inset-0 flex items-center justify-center bg-navy/45"
                                        >
                                            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-ivory/95 text-navy text-[10px]">
                                                ▶
                                            </span>
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Meta */}
                <div className="col-span-12 md:col-span-4 text-ivory flex flex-col justify-center md:pl-6">
                    <p className="overline text-terracotta mb-4">
                        {project.featured
                            ? `${t.portfolio.featuredLabel} · ${project.year}`
                            : project.year}
                    </p>
                    {project.tag && (
                        <p
                            data-testid="lightbox-tag"
                            className="overline text-ivory inline-flex items-center gap-2 border border-terracotta px-3 py-1.5 self-start mb-5"
                        >
                            <span className="text-terracotta">★</span>
                            {project.tag[lang]}
                        </p>
                    )}
                    <h3
                        data-testid="lightbox-title"
                        className="font-serif text-4xl md:text-5xl font-light leading-tight"
                    >
                        {project.title[lang]}
                    </h3>
                    <p className="mt-5 text-ivory/75 leading-relaxed">
                        {project.description[lang]}
                    </p>
                    {project.credit && (
                        <p
                            data-testid="lightbox-credit"
                            className="mt-4 font-serif italic text-terracotta"
                        >
                            {project.credit[lang]}
                        </p>
                    )}
                    <dl className="mt-8 grid grid-cols-2 gap-x-6 gap-y-4">
                        <div>
                            <dt className="overline text-ivory/45">
                                {t.portfolio.cardMeta.location}
                            </dt>
                            <dd className="text-sm text-ivory/85 mt-1">
                                {project.location[lang]}
                            </dd>
                        </div>
                        <div>
                            <dt className="overline text-ivory/45">
                                {t.portfolio.cardMeta.surface}
                            </dt>
                            <dd className="text-sm text-ivory/85 mt-1">
                                {project.surface[lang]}
                            </dd>
                        </div>
                        <div>
                            <dt className="overline text-ivory/45">
                                {t.portfolio.cardMeta.size}
                            </dt>
                            <dd className="text-sm text-ivory/85 mt-1">
                                {project.size}
                            </dd>
                        </div>
                        <div>
                            <dt className="overline text-ivory/45">
                                {t.portfolio.cardMeta.medium}
                            </dt>
                            <dd className="text-sm text-ivory/85 mt-1">
                                {project.medium[lang]}
                            </dd>
                        </div>
                    </dl>
                </div>
            </div>

            {/* Floating side arrows — vertically centered */}
            <button
                type="button"
                data-testid="lightbox-prev"
                onClick={(e) => {
                    e.stopPropagation();
                    onPrev();
                }}
                aria-label="Previous"
                className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-12 h-12 md:w-14 md:h-14 flex items-center justify-center text-ivory text-2xl bg-ivory/0 hover:bg-ivory/10 border border-ivory/20 hover:border-terracotta transition-all"
            >
                ←
            </button>
            <button
                type="button"
                data-testid="lightbox-next"
                onClick={(e) => {
                    e.stopPropagation();
                    onNext();
                }}
                aria-label="Next"
                className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-12 h-12 md:w-14 md:h-14 flex items-center justify-center text-ivory text-2xl bg-ivory/0 hover:bg-ivory/10 border border-ivory/20 hover:border-terracotta transition-all"
            >
                →
            </button>

            {/* Bottom hint (centered, leaves corners free for the Emergent badge) */}
            <div className="border-t border-ivory/10 px-6 md:px-10 py-4 flex justify-center">
                <p className="overline text-ivory/45">{hint}</p>
            </div>
        </div>
    );
}
